import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fm = (n, d = 2) => n.toLocaleString("da-DK", { minimumFractionDigits: d, maximumFractionDigits: d });
const dkJoin = (list) => (list.length > 1 ? list.slice(0, -1).join(", ") + " og " + list[list.length - 1] : list[0] || "");
const dkDato = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("da-DK", { weekday: "long", day: "numeric", month: "long" });
};

export default function PrisOversigt() {
  const [area, setArea] = useState("DK1");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [dag, setDag] = useState("idag");
  const [visTabel, setVisTabel] = useState(false);

  useEffect(() => {
    let dead = false;
    setStatus("loading");
    fetch(`${API}/elpriser?area=${area}`)
      .then((r) => {
        if (!r.ok) throw new Error("fetch failed");
        return r.json();
      })
      .then((d) => {
        if (!dead) {
          setData(d);
          setStatus("ok");
          setDag("idag");
        }
      })
      .catch(() => {
        if (!dead) setStatus("error");
      });
    return () => {
      dead = true;
    };
  }, [area]);

  const timer = data ? (dag === "imorgen" && data.imorgen ? data.imorgen.timer : data.timer) : null;
  const max = timer ? Math.max(...timer.map((t) => t.kr_kwh), 0.0001) : 1;
  const avg = timer ? timer.reduce((s, t) => s + t.kr_kwh, 0) / timer.length : 0;
  const minT = timer ? timer.reduce((a, b) => (b.kr_kwh < a.kr_kwh ? b : a)) : null;
  const maxT = timer ? timer.reduce((a, b) => (b.kr_kwh > a.kr_kwh ? b : a)) : null;
  const cheapest3 = timer ? [...timer].sort((a, b) => a.kr_kwh - b.kr_kwh).slice(0, 3).map((t) => t.kl) : [];
  const nuHour = String(new Date().getHours()).padStart(2, "0");

  return (
    <div className="meter" data-testid="prisoversigt-panel" aria-label="Elpriser time for time">
      <div className="srow">
        <b>Spotpris pr. kWh</b>
        <select aria-label="Prisområde" data-testid="priser-area-select" value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="DK1">Vestdanmark (DK1)</option>
          <option value="DK2">Østdanmark (DK2)</option>
        </select>
      </div>

      {status === "loading" && <p className="smsg">Henter priserne …</p>}
      {status === "error" && (
        <p className="smsg" data-testid="priser-error">
          Kunne ikke hente elpriserne lige nu. Prøv igen senere.
        </p>
      )}

      {status === "ok" && data && timer && (
        <>
          <div className="stabs" role="tablist" aria-label="Vælg dag">
            <button type="button" role="tab" aria-selected={dag === "idag"} className={dag === "idag" ? "on" : ""} data-testid="priser-tab-idag" onClick={() => setDag("idag")}>
              I dag
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={dag === "imorgen"}
              className={dag === "imorgen" ? "on" : ""}
              data-testid="priser-tab-imorgen"
              disabled={!data.imorgen}
              onClick={() => setDag("imorgen")}
            >
              I morgen{data.imorgen ? "" : " (kl. ca. 13)"}
            </button>
          </div>
          <p className="sdato" data-testid="priser-dato">{dkDato(dag === "imorgen" && data.imorgen ? data.imorgen.dato : data.dato)}</p>

          {dag === "idag" && data.nu && (
            <p className="snow">
              Lige nu (kl. {data.nu.kl}): <b data-testid="priser-current-price">{fm(data.nu.kr_kwh)} kr./kWh</b>
            </p>
          )}

          <div className="bars" aria-hidden="true" data-testid="priser-bars">
            {timer.map((t) => (
              <span
                key={t.kl}
                className={"bar" + (dag === "idag" && t.kl === nuHour ? " now" : "") + (cheapest3.includes(t.kl) ? " cheap" : "")}
                style={{ height: Math.max(6, (t.kr_kwh / max) * 100) + "%" }}
                title={`kl. ${t.kl}: ${fm(t.kr_kwh)} kr./kWh`}
              />
            ))}
          </div>
          <div className="slbl">
            <span>00</span>
            <span>06</span>
            <span>12</span>
            <span>18</span>
            <span>23</span>
          </div>

          <dl className="stats" aria-live="polite" style={{ marginTop: "16px" }}>
            <div>
              <dt>Laveste</dt>
              <dd data-testid="priser-min-output">{minT ? fm(minT.kr_kwh) + " kr." : "–"}</dd>
            </div>
            <div>
              <dt>Gennemsnit</dt>
              <dd data-testid="priser-avg-output">{fm(avg)} kr.</dd>
            </div>
            <div className="big">
              <dt>Højeste</dt>
              <dd data-testid="priser-max-output">{maxT ? fm(maxT.kr_kwh) + " kr." : "–"}</dd>
            </div>
          </dl>
          <p className="smsg" data-testid="priser-cheapest">
            Billigste timer: kl. {dkJoin(cheapest3)}.
          </p>

          <button type="button" className="sbtn" data-testid="priser-table-toggle" onClick={() => setVisTabel(!visTabel)}>
            {visTabel ? "Skjul alle 24 timer" : "Se alle 24 timer"}
          </button>
          {visTabel && (
            <div className="ptwrap">
              <table className="ptable" data-testid="priser-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Spotpris</th>
                  </tr>
                </thead>
                <tbody>
                  {timer.map((t) => (
                    <tr key={t.kl} className={dag === "idag" && t.kl === nuHour ? "nu" : ""}>
                      <td>kl. {t.kl}–{String((parseInt(t.kl, 10) + 1) % 24).padStart(2, "0")}</td>
                      <td>{fm(t.kr_kwh)} kr./kWh</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="sfine">
            Spotpriser fra Energinets Energi Data Service – ekskl. tariffer, afgifter og moms. Gul søjle er den aktuelle
            time, grønne er de tre billigste. <a href="/beregner/">Brug priserne i beregneren</a>.
          </p>
        </>
      )}
    </div>
  );
}
